import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, ShoppingCart, User } from 'lucide-react';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AppFooter from './AppFooter';
import CookieConsentBanner from '../cookies/CookieConsentBanner';
import LanguageSelector from '../LanguageSelector';
import CurrencySelector from '../CurrencySelector';
import i18n from '../../i18n';
import { useCurrency } from '../../context/CurrencyContext';
import FeedbackFab from '../feedback/FeedbackFab';
import FirstVisitHelpPrompt from '../storefront/ScrollDiscoveryPrompt';
import { useStorefrontTraffic } from '../../hooks/useStorefrontTraffic';
import ReferralFab from '../referrals/ReferralFab';
import { DEFAULT_LANGUAGE, type LanguageCode } from '../../config/locale';
import {
  buildCustomerRegisterUrl,
  buildLoginUrl,
  getReturnPathForAuthRedirect,
} from '../../utils/authReturnUrl';
import {
  FALLBACK_STOREFRONT_THEME,
  hexWithAlpha,
  parseStorefrontThemeFromSiteContent,
  type StorefrontChromeTheme,
} from '../../utils/storefrontTheme';
import { STOREFRONT_HEADER_SPACER_FALLBACK_CLASS } from './storefrontLayoutConstants';
import { LewouhmartLogoMark } from '../branding/LewouhmartLogoMark';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';
import {
  departmentPath,
  normalizeDepartmentSlug,
  storefrontCatalogPathSuffix,
} from '../../utils/departmentRouting';
import { wsPath } from '../../utils/siteHosts';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const DEFAULT_PUBLIC_CONTACT = {
  address: '123 Street, New York, USA',
  email: 'info@example.com',
};

const DEFAULT_SOCIAL_LINKS = {
  socialFacebookUrl: 'https://facebook.com',
  socialInstagramUrl: 'https://instagram.com',
  socialLinkedinUrl: 'https://linkedin.com',
  socialXUrl: 'https://x.com',
};

type StorefrontLayoutProps = {
  children: ReactNode;
  /** Classes on <main> (e.g. bg-slate-50) */
  mainClassName?: string;
  /** Legacy: main no longer uses extra top padding; prop kept for call-site compatibility. */
  mainFlushTop?: boolean;
  /** Wrap children in max-w-6xl horizontal padding (account-style pages) */
  contentContained?: boolean;
  showFooter?: boolean;
  /**
   * When the page uses a fixed left rail (e.g. customer account `lg:w-52`), offset the footer
   * so it only spans the main column — same width as `lg:ml-52` content.
   */
  footerAccountRailOffset?: boolean;
};

type SiteContentSocials = {
  socialFacebookUrl?: string;
  socialInstagramUrl?: string;
  socialLinkedinUrl?: string;
  socialXUrl?: string;
};

type StorefrontHeaderProps = {
  theme: StorefrontChromeTheme;
  publicContact: { address: string; email: string };
  socialLinks: Required<SiteContentSocials>;
};

const StorefrontHeader = React.forwardRef<HTMLDivElement, StorefrontHeaderProps>(
  function StorefrontHeader({ theme, publicContact, socialLinks }, ref) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    auth,
    logout,
    fetchPreferences,
    updatePreferences,
  } = useAuth();
  const { lines } = useCart();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();
  const isCustomer = isAuthenticated && auth.type === 'CUSTOMER';
  const accountPath =
    isCustomer ? '/account' : isAuthenticated ? wsPath('/workspace') : '/auth';
  const pathSegment = location.pathname.split('/').filter(Boolean)[0] || '';
  /** Cart/checkout: hide department switcher — user is finishing a purchase, not browsing. */
  const hideDepartmentSwitcher =
    /\/cart\/?$/.test(location.pathname) || /\/checkout\/?$/.test(location.pathname);
  const inDepartmentContext =
    ![
      '',
      'about',
      'feedback',
      'legal',
      'products',
      'packages',
      'cart',
      'checkout',
      'auth',
      'account',
      'workspace',
      'department-management',
    ].includes(pathSegment);
  const catalogPathSuffix = storefrontCatalogPathSuffix(location.pathname);
  const activeDepartmentSlug = inDepartmentContext
    ? normalizeDepartmentSlug(pathSegment)
    : null;
  const storefrontHomePath = inDepartmentContext ? departmentPath(activeDepartmentSlug, '') : '/';
  const storefrontProductsPath = inDepartmentContext
    ? departmentPath(activeDepartmentSlug, '/products')
    : catalogPathSuffix.startsWith('/products')
      ? catalogPathSuffix
      : '/products';
  const storefrontPackagesPath = inDepartmentContext
    ? departmentPath(activeDepartmentSlug, '/packages')
    : catalogPathSuffix.startsWith('/packages')
      ? catalogPathSuffix
      : '/packages';
  const storefrontCartPath = '/cart';
  const [publicDepartments, setPublicDepartments] = useState<Array<{ slug: string; name: string }>>([]);

  const cartDistinctCount = lines.length;
  const cartBadgeLabel =
    cartDistinctCount > 99 ? '99+' : cartDistinctCount > 0 ? String(cartDistinctCount) : '';

  const [language, setLanguage] = useState<LanguageCode>(() => {
    try {
      const v = window.localStorage.getItem('lewouhmart_lang') as LanguageCode | null;
      return v && ['en', 'fr', 'es', 'de'].includes(v) ? v : DEFAULT_LANGUAGE;
    } catch {
      return DEFAULT_LANGUAGE;
    }
  });
  const [navSearch, setNavSearch] = useState(() => {
    try {
      return window.localStorage.getItem('lewouhmart_nav_search') || '';
    } catch {
      return '';
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem('lewouhmart_nav_search', navSearch);
    } catch {
      // ignore
    }
  }, [navSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    if (q) setNavSearch(q);
  }, [location.search]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/departments/public`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ slug?: string; name?: string }>;
        if (!cancelled) {
          setPublicDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                slug: normalizeDepartmentSlug(row.slug || ''),
                name: String(row.name || row.slug || '').trim() || normalizeDepartmentSlug(row.slug || ''),
              }))
              .filter((row) => Boolean(row.slug)),
          );
        }
      } catch {
        if (!cancelled) setPublicDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDepartmentFilterChange = (value: string) => {
    const selected = String(value || '').trim().toLowerCase();
    const suffix = storefrontCatalogPathSuffix(location.pathname);

    if (!selected) {
      if (suffix.startsWith('/products')) {
        const rest = suffix.slice('/products'.length) || '';
        navigate({ pathname: `/products${rest}`, search: location.search });
        return;
      }
      if (suffix.startsWith('/packages')) {
        const rest = suffix.slice('/packages'.length) || '';
        navigate({ pathname: `/packages${rest}`, search: location.search });
        return;
      }
      navigate('/');
      return;
    }

    const deptBase = `/${selected}`;
    if (suffix.startsWith('/products')) {
      const rest = suffix.slice('/products'.length) || '';
      navigate({ pathname: `${deptBase}/products${rest}`, search: location.search });
      return;
    }
    if (suffix.startsWith('/packages')) {
      const rest = suffix.slice('/packages'.length) || '';
      navigate({ pathname: `${deptBase}/packages${rest}`, search: location.search });
      return;
    }

    navigate(deptBase);
  };

  useEffect(() => {
    const syncFromI18n = () => {
      const raw = i18n.resolvedLanguage || i18n.language || 'en';
      const code = (raw.split('-')[0] || 'en') as LanguageCode;
      if (code === 'en' || code === 'fr' || code === 'es' || code === 'de') {
        setLanguage((prev) => (prev === code ? prev : code));
      }
    };
    i18n.on('languageChanged', syncFromI18n);
    syncFromI18n();
    return () => {
      i18n.off('languageChanged', syncFromI18n);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    fetchPreferences()
      .then((prefs) => {
        if (!prefs || cancelled) return;
        if (prefs.language) {
          const lang = prefs.language as LanguageCode;
          setLanguage(lang);
          window.localStorage.setItem('lewouhmart_lang', lang);
          i18n.changeLanguage(lang).catch(() => {});
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [fetchPreferences, isAuthenticated]);

  const handleLanguageChange = (value: string) => {
    const lang = value as LanguageCode;
    setLanguage(lang);
    window.localStorage.setItem('lewouhmart_lang', lang);
    i18n.changeLanguage(lang).catch(() => {});
    if (isAuthenticated) {
      updatePreferences({ language: lang }).catch(() => {});
    }
  };

  const navBorder = 'rgba(148, 163, 184, 0.55)';
  const softBrandBg = hexWithAlpha(theme.brandPrimary, '14');
  const softBrandBgHover = hexWithAlpha(theme.brandPrimary, '26');
  const softAccentBg = hexWithAlpha(theme.navAccent, '14');
  const softAccentBgHover = hexWithAlpha(theme.navAccent, '26');
  const searchFocusBg = hexWithAlpha(theme.brandPrimary, '0d');
  const searchFocusBorder = hexWithAlpha(theme.brandPrimary, '66');

  const cssVars = {
    '--sf-fg': theme.navFg,
    '--sf-accent': theme.navAccent,
    '--sf-brand': theme.brandPrimary,
  } as React.CSSProperties;

  return (
    <div
      ref={ref}
      className="fixed inset-x-0 top-0 z-40 shadow-sm backdrop-blur-md"
      style={cssVars}
    >
      <div
        className="hidden border-b lg:block"
        style={{ backgroundColor: theme.navTopBg, borderBottomColor: navBorder }}
      >
        <div
          className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-1.5 text-[10px] sm:px-5 lg:px-6 xl:px-8"
          style={{ color: theme.navFg, opacity: 0.92 }}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 sm:flex-initial">
            <span className="inline-flex max-w-[14rem] items-center gap-1.5 truncate xl:max-w-none">
              <i className="fa fa-map-marker-alt shrink-0" style={{ color: theme.brandPrimary }} />
              <span className="truncate">{publicContact.address}</span>
            </span>
            <span className="hidden items-center gap-1.5 sm:inline-flex">
              <i className="fa fa-envelope" style={{ color: theme.brandPrimary }} />
              {publicContact.email}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span>{t('storefront.header_follow')}</span>
            <div className="flex items-center gap-2" style={{ color: theme.navFg, opacity: 0.75 }}>
              <a
                href={socialLinks.socialFacebookUrl}
                target="_blank"
                rel="noreferrer"
                className="transition-colors hover:opacity-100"
                style={{ color: 'inherit' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.brandPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
                aria-label={t('storefront.aria_facebook')}
              >
                <i className="fab fa-facebook-f" />
              </a>
              <a
                href={socialLinks.socialXUrl}
                target="_blank"
                rel="noreferrer"
                className="transition-colors"
                style={{ color: 'inherit' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.brandPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
                aria-label={t('storefront.aria_twitter')}
              >
                <i className="fab fa-twitter" />
              </a>
              <a
                href={socialLinks.socialLinkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="transition-colors"
                style={{ color: 'inherit' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.brandPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
                aria-label={t('storefront.aria_linkedin')}
              >
                <i className="fab fa-linkedin-in" />
              </a>
              <a
                href={socialLinks.socialInstagramUrl}
                target="_blank"
                rel="noreferrer"
                className="transition-colors"
                style={{ color: 'inherit' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.brandPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '';
                }}
                aria-label={t('storefront.aria_instagram')}
              >
                <i className="fab fa-instagram" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b" style={{ backgroundColor: theme.navBg, borderBottomColor: navBorder }}>
        <div className="flex w-full min-w-0 flex-col gap-2 px-4 py-2 sm:px-5 lg:flex-row lg:flex-nowrap lg:items-center lg:gap-x-2 lg:px-6 xl:px-8">
          <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-2 lg:min-w-0 lg:flex-1 lg:flex-nowrap lg:justify-start lg:gap-x-2 lg:gap-y-0 lg:overflow-hidden">
            <div className="flex min-w-0 shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => navigate(storefrontHomePath)}
                className="flex shrink-0 items-center gap-1.5"
                aria-label={t('header.nav_home')}
              >
                <LewouhmartLogoMark
                  brandPrimary={theme.brandPrimary}
                  navAccent={theme.navAccent}
                  size={30}
                  className="shadow-sm ring-1 ring-black/[0.08]"
                />
                <span
                  className="truncate text-base font-extrabold tracking-tight xl:text-lg"
                  style={{ color: theme.brandPrimary }}
                >
                  Lewouh<span style={{ color: theme.navAccent }}>mart</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border lg:hidden"
                style={{
                  borderColor: navBorder,
                  backgroundColor: theme.navTopBg,
                  color: theme.navFg,
                }}
                aria-label={t('storefront.nav_site_label')}
              >
                <Menu className="h-4 w-4" />
              </button>
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3 lg:hidden">
              {isAuthenticated && !isCustomer ? (
                <button
                  type="button"
                  onClick={() => navigate(accountPath)}
                  className="btn-sm-square"
                  title={t('header.account_button')}
                >
                  <User className="h-4 w-4" style={{ color: theme.navFg }} />
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => navigate(storefrontCartPath)}
                className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-colors hover:bg-black/[0.03]"
                style={{
                  borderColor: navBorder,
                  backgroundColor: theme.navBg,
                  color: theme.navFg,
                }}
                title={
                  cartDistinctCount > 0
                    ? t('storefront.cart_with_items_title', { count: cartDistinctCount })
                    : t('header.nav_cart')
                }
                aria-label={
                  cartDistinctCount > 0
                    ? t('storefront.cart_aria_with_count', { count: cartDistinctCount })
                    : t('storefront.cart_aria_empty')
                }
              >
                <ShoppingCart
                  className="h-[18px] w-[18px] shrink-0"
                  style={{ color: theme.navFg }}
                  strokeWidth={2.25}
                  aria-hidden
                />
                {cartDistinctCount > 0 ? (
                  <span
                    className={[
                      'pointer-events-none absolute z-10 flex items-center justify-center rounded-full bg-[#e53935] font-bold tabular-nums leading-none text-white shadow-sm ring-2 ring-[color:var(--cart-ring)]',
                      cartDistinctCount > 99
                        ? 'right-0 top-0 min-h-[1.125rem] min-w-[1.5rem] translate-x-[40%] -translate-y-[40%] px-1 text-[9px]'
                        : 'right-0 top-0 h-[1.125rem] min-w-[1.125rem] translate-x-[35%] -translate-y-[35%] text-[11px]',
                    ].join(' ')}
                    style={{ ['--cart-ring' as string]: theme.navBg } as React.CSSProperties}
                  >
                    {cartBadgeLabel}
                  </span>
                ) : null}
              </button>

              {isCustomer ? (
                <button
                  type="button"
                  onClick={() => logout()}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100"
                  title={t('header.logout_button')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <div
              className="hidden min-w-0 items-center lg:flex lg:flex-nowrap lg:overflow-hidden"
              style={cssVars}
            >
              <nav
                className="flex min-w-0 flex-nowrap items-center gap-x-1.5 xl:gap-x-2"
                aria-label={t('storefront.nav_site_label')}
              >
              <NavLink
                to={storefrontHomePath}
                end
                className={({ isActive }) =>
                  `shrink-0 border-b-2 border-solid px-0.5 py-0.5 text-xs font-medium transition-colors ${
                    isActive ? '' : 'border-transparent'
                  }`
                }
                style={({ isActive }) => ({
                  borderBottomColor: isActive ? theme.navAccent : 'transparent',
                  color: isActive ? theme.navAccent : theme.navFg,
                })}
              >
                {t('header.nav_home')}
              </NavLink>
              <a
                href="/about"
                className="shrink-0 border-b-2 border-transparent border-solid px-0.5 py-0.5 text-xs font-medium transition-colors hover:border-[color:var(--sf-accent)] hover:text-[color:var(--sf-accent)]"
                style={{ color: theme.navFg }}
              >
                {t('storefront.nav_about')}
              </a>
              <NavLink
                to={storefrontProductsPath}
                className={({ isActive }) =>
                  `shrink-0 border-b-2 border-solid px-0.5 py-0.5 text-xs font-medium transition-colors ${
                    isActive ? '' : 'border-transparent'
                  }`
                }
                style={({ isActive }) => ({
                  borderBottomColor: isActive ? theme.navAccent : 'transparent',
                  color: isActive ? theme.navAccent : theme.navFg,
                })}
              >
                {t('storefront.nav_products')}
              </NavLink>
              <NavLink
                to={storefrontPackagesPath}
                className={({ isActive }) =>
                  `shrink-0 border-b-2 border-solid px-0.5 py-0.5 text-xs font-medium transition-colors ${
                    isActive ? '' : 'border-transparent'
                  }`
                }
                style={({ isActive }) => ({
                  borderBottomColor: isActive ? theme.navAccent : 'transparent',
                  color: isActive ? theme.navAccent : theme.navFg,
                })}
              >
                {t('storefront.nav_packages', { defaultValue: 'Packages' })}
              </NavLink>
              <a
                href="/feedback"
                className="shrink-0 border-b-2 border-transparent border-solid px-0.5 py-0.5 text-xs font-medium transition-colors hover:border-[color:var(--sf-accent)] hover:text-[color:var(--sf-accent)]"
                style={{ color: theme.navFg }}
              >
                {t('storefront.nav_contact')}
              </a>
              {!hideDepartmentSwitcher ? (
                <div className="ml-0.5 shrink-0">
                  <select
                    value={inDepartmentContext && activeDepartmentSlug ? activeDepartmentSlug : ''}
                    onChange={(e) => handleDepartmentFilterChange(e.target.value)}
                    className="max-w-[6.5rem] rounded-md border border-slate-400/40 bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold outline-none xl:max-w-[7.5rem]"
                    style={{ color: theme.navFg }}
                    title="All departments"
                  >
                    <option value="" style={{ color: '#0f172a' }}>
                      All depts
                    </option>
                    {publicDepartments.map((dep) => (
                      <option key={dep.slug} value={dep.slug} style={{ color: '#0f172a' }}>
                        {dep.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              </nav>
            </div>
          </div>

          <div className="flex w-full min-w-0 shrink-0 items-center gap-1.5 sm:gap-2 lg:ml-auto lg:w-auto">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const q = navSearch.trim();
              navigate(
                q
                  ? `${storefrontProductsPath}?q=${encodeURIComponent(q)}`
                  : storefrontProductsPath,
              );
            }}
            className="group flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-full border px-2.5 transition-all duration-300 ease-out focus-within:shadow-sm lg:w-[9.5rem] lg:flex-none xl:w-[11rem]"
            style={{
              borderColor: navBorder,
              backgroundColor: theme.navTopBg,
            }}
            onFocusCapture={(e) => {
              const el = e.currentTarget;
              el.style.borderColor = searchFocusBorder;
              el.style.backgroundColor = searchFocusBg;
            }}
            onBlurCapture={(e) => {
              const el = e.currentTarget;
              if (el.contains(e.relatedTarget as Node | null)) return;
              el.style.borderColor = navBorder;
              el.style.backgroundColor = theme.navTopBg;
            }}
          >
            <i
              className="fa fa-search shrink-0 pl-0.5 text-xs text-slate-400 transition-colors group-focus-within:text-[color:var(--sf-brand)]"
              style={cssVars}
              aria-hidden
            />
            <input
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder={t('storefront.products_search_placeholder')}
              className="min-w-0 flex-1 bg-transparent py-0 text-xs outline-none placeholder:text-slate-400"
              style={{ color: theme.navFg }}
            />
            {navSearch.trim() ? (
              <button
                type="button"
                onClick={() => {
                  setNavSearch('');
                  navigate(storefrontProductsPath);
                }}
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                aria-label={t('common.clear')}
                title={t('common.clear')}
              >
                <i className="fa fa-times text-[10px]" />
              </button>
            ) : null}
          </form>

          <div className="hidden shrink-0 items-center gap-1.5 lg:flex lg:flex-nowrap">
            {isCustomer ? (
              <div
                className="flex shrink-0 items-center gap-1 border-r pr-1.5"
                style={{ borderRightColor: navBorder }}
                aria-label={t('storefront.nav_account_label')}
              >
                <button
                  type="button"
                  onClick={() => navigate('/account')}
                  title={t('storefront.nav_my_profile')}
                  className="inline-flex h-8 items-center gap-1 rounded-full px-2 text-[10px] font-semibold transition-colors xl:px-2.5 xl:text-[11px]"
                  style={{
                    backgroundColor: softBrandBg,
                    color: theme.brandPrimary,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = softBrandBgHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = softBrandBg;
                  }}
                >
                  <User className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                  <span className="hidden xl:inline">{t('storefront.nav_my_profile')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/account/orders')}
                  title={t('storefront.nav_my_orders')}
                  className="inline-flex h-8 items-center rounded-full px-2 text-[10px] font-semibold transition-colors xl:px-2.5 xl:text-[11px]"
                  style={{
                    backgroundColor: softAccentBg,
                    color: theme.navAccent,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = softAccentBgHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = softAccentBg;
                  }}
                >
                  <span className="hidden xl:inline">{t('storefront.nav_my_orders')}</span>
                  <span className="xl:hidden">Orders</span>
                </button>
              </div>
            ) : null}
            <LanguageSelector
              variant="storefront"
              value={language}
              onChange={handleLanguageChange}
            />
            <CurrencySelector
              variant="storefront"
              value={currency}
              options={supportedCurrencies}
              onChange={setCurrency}
            />

            {!isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate(buildLoginUrl(getReturnPathForAuthRedirect()))}
                  className="hidden h-8 rounded-full border bg-white px-2.5 text-[10px] font-semibold sm:inline xl:px-3"
                  style={{
                    borderColor: theme.brandPrimary,
                    color: theme.brandPrimary,
                  }}
                >
                  {t('auth.tab_sign_in')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(buildCustomerRegisterUrl())}
                  className="hidden h-8 rounded-full px-2.5 text-[10px] font-semibold text-white md:inline xl:px-3"
                  style={{ backgroundColor: theme.navAccent }}
                >
                  {t('auth.tab_register')}
                </button>
              </>
            ) : null}

            {isAuthenticated && !isCustomer ? (
              <button
                type="button"
                onClick={() => navigate(accountPath)}
                className="btn-sm-square"
                title={t('header.account_button')}
              >
                <User className="h-4 w-4" style={{ color: theme.navFg }} />
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => navigate(storefrontCartPath)}
              className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-colors hover:bg-black/[0.03]"
              style={{
                borderColor: navBorder,
                backgroundColor: theme.navBg,
                color: theme.navFg,
              }}
              title={
                cartDistinctCount > 0
                  ? t('storefront.cart_with_items_title', { count: cartDistinctCount })
                  : t('header.nav_cart')
              }
              aria-label={
                cartDistinctCount > 0
                  ? t('storefront.cart_aria_with_count', { count: cartDistinctCount })
                  : t('storefront.cart_aria_empty')
              }
            >
              <ShoppingCart
                className="h-[18px] w-[18px] shrink-0"
                style={{ color: theme.navFg }}
                strokeWidth={2.25}
                aria-hidden
              />
              {cartDistinctCount > 0 ? (
                <span
                  className={[
                    'pointer-events-none absolute z-10 flex items-center justify-center rounded-full bg-[#e53935] font-bold tabular-nums leading-none text-white shadow-sm ring-2 ring-[color:var(--cart-ring)]',
                    cartDistinctCount > 99
                      ? 'right-0 top-0 min-h-[1.125rem] min-w-[1.5rem] translate-x-[40%] -translate-y-[40%] px-1 text-[9px]'
                      : 'right-0 top-0 h-[1.125rem] min-w-[1.125rem] translate-x-[35%] -translate-y-[35%] text-[11px]',
                  ].join(' ')}
                  style={{ ['--cart-ring' as string]: theme.navBg } as React.CSSProperties}
                >
                  {cartBadgeLabel}
                </span>
              ) : null}
            </button>

            {isCustomer ? (
              <button
                type="button"
                onClick={() => logout()}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100"
                title={t('header.logout_button')}
              >
                <LogOut className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          </div>
        </div>
      </div>
      {typeof document !== 'undefined' && mobileNavOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[110] flex lg:hidden"
              role="dialog"
              aria-modal="true"
              aria-label={t('storefront.nav_site_label')}
            >
              <button
                type="button"
                className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px]"
                onClick={() => setMobileNavOpen(false)}
                aria-label={t('common.close', { defaultValue: 'Close' })}
              />
              <div
                className="relative ml-auto flex h-[100dvh] max-h-[100dvh] w-[min(22rem,100vw)] flex-col border-l p-4 shadow-2xl"
                style={{
                  backgroundColor: theme.navBg,
                  borderLeftColor: navBorder,
                  color: theme.navFg,
                }}
              >
                <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileNavOpen(false);
                      navigate(storefrontHomePath);
                    }}
                    className="inline-flex min-w-0 items-center gap-2"
                    aria-label={t('header.nav_home')}
                  >
                    <LewouhmartLogoMark
                      brandPrimary={theme.brandPrimary}
                      navAccent={theme.navAccent}
                      size={28}
                      className="shadow-sm ring-1 ring-black/[0.08]"
                    />
                    <span
                      className="truncate text-base font-extrabold tracking-tight"
                      style={{ color: theme.brandPrimary }}
                    >
                      Lewouh<span style={{ color: theme.navAccent }}>mart</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileNavOpen(false)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border"
                    style={{ borderColor: navBorder }}
                    aria-label={t('common.close', { defaultValue: 'Close' })}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <nav
                  className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain"
                  aria-label={t('storefront.nav_site_label')}
                >
                  {[
                    { label: t('header.nav_home'), to: storefrontHomePath },
                    { label: t('storefront.nav_about'), to: '/about' },
                    { label: t('storefront.nav_products'), to: storefrontProductsPath },
                    {
                      label: t('storefront.nav_packages', { defaultValue: 'Packages' }),
                      to: storefrontPackagesPath,
                    },
                    { label: t('storefront.nav_contact'), to: '/feedback' },
                  ].map((entry) => (
                    <button
                      key={entry.to}
                      type="button"
                      onClick={() => {
                        setMobileNavOpen(false);
                        navigate(entry.to);
                      }}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-semibold hover:bg-black/5"
                    >
                      {entry.label}
                    </button>
                  ))}
                </nav>
                {!hideDepartmentSwitcher ? (
                  <div className="mt-3 shrink-0 border-t pt-3" style={{ borderTopColor: navBorder }}>
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-wide opacity-80">
                      Departments
                    </p>
                    <select
                      value={inDepartmentContext && activeDepartmentSlug ? activeDepartmentSlug : ''}
                      onChange={(e) => {
                        setMobileNavOpen(false);
                        handleDepartmentFilterChange(e.target.value);
                      }}
                      className="w-full rounded-lg border px-3 py-2 text-sm font-semibold"
                      style={{
                        borderColor: navBorder,
                        backgroundColor: theme.navTopBg,
                        color: theme.navFg,
                      }}
                    >
                      <option value="" style={{ color: '#0f172a' }}>
                        All departments
                      </option>
                      {publicDepartments.map((dep) => (
                        <option key={dep.slug} value={dep.slug} style={{ color: '#0f172a' }}>
                          {dep.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div
                  className="mt-4 shrink-0 border-t pt-4"
                  style={{ borderTopColor: navBorder }}
                >
                  <p
                    className="mb-2 text-[11px] font-bold uppercase tracking-wide opacity-80"
                    style={{ color: theme.navFg }}
                  >
                    {t('common.language')}
                  </p>
                  <LanguageSelector
                    variant="storefront"
                    value={language}
                    onChange={handleLanguageChange}
                    fullWidth
                  />
                </div>
                <div
                  className="mt-4 shrink-0 border-t pt-4"
                  style={{ borderTopColor: navBorder }}
                >
                  <p
                    className="mb-2 text-[11px] font-bold uppercase tracking-wide opacity-80"
                    style={{ color: theme.navFg }}
                  >
                    {t('common.currency')}
                  </p>
                  <CurrencySelector
                    variant="storefront"
                    value={currency}
                    options={supportedCurrencies}
                    onChange={setCurrency}
                    fullWidth
                  />
                </div>
                <div
                  className="mt-auto shrink-0 border-t pt-3"
                  style={{ borderTopColor: navBorder }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMobileNavOpen(false);
                      navigate(accountPath);
                    }}
                    className="mb-2 flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold"
                    style={{ backgroundColor: softBrandBg, color: theme.brandPrimary }}
                  >
                    {isAuthenticated ? t('header.account_button') : t('auth.tab_sign_in')}
                  </button>
                  {!isAuthenticated ? (
                    <button
                      type="button"
                      onClick={() => {
                        setMobileNavOpen(false);
                        navigate(buildCustomerRegisterUrl());
                      }}
                      className="flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
                      style={{ backgroundColor: theme.navAccent }}
                    >
                      {t('auth.tab_register')}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
});

StorefrontHeader.displayName = 'StorefrontHeader';

/**
 * Shared shell for the public store: same header as the customer account area,
 * cart badge on the bag icon, and optional full marketing footer.
 */
export function StorefrontLayout({
  children,
  mainClassName = '',
  mainFlushTop: _mainFlushTop = false,
  contentContained = false,
  showFooter = true,
  footerAccountRailOffset = false,
}: StorefrontLayoutProps) {
  const { i18n } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerOffsetPx, setHeaderOffsetPx] = useState(0);
  useStorefrontTraffic();

  const [chrome, setChrome] = useState({
    theme: FALLBACK_STOREFRONT_THEME,
    publicContact: DEFAULT_PUBLIC_CONTACT,
    socialLinks: DEFAULT_SOCIAL_LINKS,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/public/site-content`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) return;
        const data = (await res.json()) as SiteContentSocials & {
          publicContactAddress?: string;
          publicContactEmail?: string;
          storefrontThemeBodyBg?: string;
          storefrontThemeNavBg?: string;
          storefrontThemeNavTopBg?: string;
          storefrontThemeNavFg?: string;
          storefrontThemeNavAccent?: string;
          storefrontThemeBrandPrimary?: string;
        };
        if (cancelled) return;
        setChrome({
          theme: parseStorefrontThemeFromSiteContent(data),
          publicContact: {
            address:
              String(data.publicContactAddress || '').trim() || DEFAULT_PUBLIC_CONTACT.address,
            email: String(data.publicContactEmail || '').trim() || DEFAULT_PUBLIC_CONTACT.email,
          },
          socialLinks: {
            socialFacebookUrl:
              data.socialFacebookUrl?.trim() || DEFAULT_SOCIAL_LINKS.socialFacebookUrl,
            socialInstagramUrl:
              data.socialInstagramUrl?.trim() || DEFAULT_SOCIAL_LINKS.socialInstagramUrl,
            socialLinkedinUrl:
              data.socialLinkedinUrl?.trim() || DEFAULT_SOCIAL_LINKS.socialLinkedinUrl,
            socialXUrl: data.socialXUrl?.trim() || DEFAULT_SOCIAL_LINKS.socialXUrl,
          },
        });
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  /** Override global `body` gradient (index.css) so admin page background is visible. */
  useEffect(() => {
    const bg = chrome.theme.bodyBg;
    document.documentElement.style.setProperty('background-color', bg);
    document.body.style.setProperty('background-color', bg);
    document.body.style.setProperty('background-image', 'none');
    return () => {
      document.documentElement.style.removeProperty('background-color');
      document.body.style.removeProperty('background-color');
      document.body.style.removeProperty('background-image');
    };
  }, [chrome.theme.bodyBg]);

  /** Spacer must match measured fixed header height or a visible strip appears below the nav. */
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setHeaderOffsetPx(h);
      document.documentElement.style.setProperty('--storefront-header-h', `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
      document.documentElement.style.removeProperty('--storefront-header-h');
    };
  }, []);

  return (
    <div
      className="app-motion-root flex min-h-screen flex-col"
      style={
        {
          backgroundColor: chrome.theme.bodyBg,
          ['--storefront-body-bg' as string]: chrome.theme.bodyBg,
          ['--storefront-brand-primary' as string]: chrome.theme.brandPrimary,
          ['--storefront-nav-accent' as string]: chrome.theme.navAccent,
          ['--storefront-nav-fg' as string]: chrome.theme.navFg,
        } as React.CSSProperties
      }
    >
      <StorefrontHeader
        ref={headerRef}
        theme={chrome.theme}
        publicContact={chrome.publicContact}
        socialLinks={chrome.socialLinks}
      />
      <div
        aria-hidden
        className={
          headerOffsetPx > 0 ? 'shrink-0' : `shrink-0 ${STOREFRONT_HEADER_SPACER_FALLBACK_CLASS}`
        }
        style={headerOffsetPx > 0 ? { height: headerOffsetPx } : undefined}
      />

      <main
        className={`flex flex-1 flex-col pb-10 pt-0 ${mainClassName || 'bg-transparent'}`.trim()}
      >
        {contentContained ? (
          <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
            {children}
          </div>
        ) : (
          children
        )}
      </main>

      {showFooter ? <AppFooter accountChromeOffset={Boolean(footerAccountRailOffset)} /> : null}
      <ReferralFab />
      <FeedbackFab />
      <FirstVisitHelpPrompt />
      <CookieConsentBanner />
    </div>
  );
}

export default StorefrontLayout;
